import { useCallback, useEffect, useRef, useState } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { useDispatch, useSelector } from 'react-redux'
import { logout } from '../actions/attendant'
import { shouldDisplayOfflineTransactionHeader } from '../selectors/order'
import { setRoute } from '../VNRoutes/ActionCreators'
import { getActiveAttendantId } from '../selectors/attendant'

const LOGOUT_MILLISECONDS = 10_800_000 // 3 hours

/**
 * This hook is used to automatically logout after 3 hours of inactivity.
 *
 * @link https://airtable.com/app2mE1mTl0c4GAir/tbl4lm32cBJ1JL2IG/viwDYQ5enWzA7pJyM/rec7T9gGvepdFpf2n
 */
const useIdleLogout = () => {
  const useTimer = useRef(null)
  const dispatch = useDispatch()
  const [shouldLogout, setShouldLogout] = useState(false)
  const isAttendantLoggedIn = useSelector(getActiveAttendantId)
  const hasOfflineTransactions = useSelector(shouldDisplayOfflineTransactionHeader)

  /**
   * Execute logout if idle, but not if syncing
   */
  const executeLogout = useCallback(async () => {
    if (hasOfflineTransactions) {
      // App is idle, but syncing. Resets to logout in the next cycle
      // it's unlikely that we would still be syncing after 3 hours
      useTimer.current.reset()
      setShouldLogout(false)
      return
    }

    // Already logged out
    if (!isAttendantLoggedIn) return

    // Automatically logout after a 3 hours of inactivity
    dispatch(logout())
    dispatch(setRoute('/user-login'))
    setShouldLogout(false)
    return
  }, [dispatch, isAttendantLoggedIn, hasOfflineTransactions])

  // Execute logout if idle
  useEffect(() => {
    if (shouldLogout) {
      executeLogout()
    }
  }, [executeLogout, shouldLogout])

  // Idle Timer Setup
  useTimer.current = useIdleTimer({
    timeout: LOGOUT_MILLISECONDS,
    onIdle: () => setShouldLogout(true),
    onActive: () => setShouldLogout(false),
    debounce: 500,
  })
}

export default useIdleLogout
